import { useEffect, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCompanyAgreements,
  useCompanyWebsites,
  useCreateAgreement,
  useCurrentAgreement,
  useUpdateAgreement,
} from '@/lib/adminApi';
import type { AgreementSummary } from '@ventin/shared';

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}
function dateInputToIso(d: string): string {
  return `${d}T00:00:00.000Z`;
}

/**
 * AgreementCard — the current agreement, edited in place (owner spec
 * 2026-06-11: no dialog, plain-English labels, optional link to one of the
 * company's websites). Past agreements render below as read-only history.
 */
export function AgreementCard({ companyId }: { companyId: string }) {
  const { data: currentData } = useCurrentAgreement(companyId);
  const { data: historyData } = useCompanyAgreements(companyId);
  const { data: websitesData } = useCompanyWebsites(companyId);
  const createMut = useCreateAgreement();
  const updateMut = useUpdateAgreement();

  const active: AgreementSummary | null = currentData?.agreement ?? null;
  const websites = websitesData?.websites ?? [];
  const history = useMemo(() => {
    const list = historyData?.agreements ?? [];
    return list.filter((a) => !a.isActive).sort((a, b) => b.startDate.localeCompare(a.startDate));
  }, [historyData]);

  const [editing, setEditing] = useState(false);
  const [includedIncrements, setIncludedIncrements] = useState('12');
  const [customRate, setCustomRate] = useState('');
  const [cycleDay, setCycleDay] = useState('27');
  const [startDate, setStartDate] = useState('');
  const [websiteId, setWebsiteId] = useState('');
  const [gaPropertyId, setGaPropertyId] = useState('');
  const [busy, setBusy] = useState(false);

  const showForm = editing || !active;

  useEffect(() => {
    if (active) {
      setIncludedIncrements(String(active.includedIncrements));
      setCustomRate(active.customRate);
      setCycleDay(String(active.cycleDay));
      setStartDate(isoToDateInput(active.startDate));
      setWebsiteId(active.websiteId ?? '');
      setGaPropertyId(active.gaPropertyId ?? '');
    } else {
      setIncludedIncrements('12');
      setCustomRate('60.00');
      setCycleDay('27');
      setStartDate(new Date().toISOString().slice(0, 10));
      setWebsiteId('');
      setGaPropertyId('');
    }
  }, [active, editing]);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        includedIncrements: Number(includedIncrements),
        customRate: customRate.trim(),
        cycleDay: Number(cycleDay),
        startDate: dateInputToIso(startDate),
        websiteId: websiteId || null,
        ...(gaPropertyId.trim() ? { gaPropertyId: gaPropertyId.trim() } : {}),
      };
      if (active) {
        await updateMut.mutateAsync({ id: active.id, patch: payload });
      } else {
        await createMut.mutateAsync({ companyId, input: payload });
      }
      toast.success('Agreement saved');
      setEditing(false);
    } catch {
      toast.error("Couldn't save — check the fields and try again");
    } finally {
      setBusy(false);
    }
  }

  const websiteName = (id: string | null) => {
    if (!id) return null;
    const w = websites.find((x) => x.id === id);
    return w ? (w.label ?? w.url) : null;
  };

  return (
    <>
      <section className="mb-8">
        <h3 className="text-sm font-medium uppercase text-muted-foreground mb-2">
          Current Agreement
        </h3>
        <div className="relative rounded-md border bg-background p-4">
          {!showForm && active && (
            <>
              <button
                type="button"
                aria-label="Edit agreement"
                title="Edit agreement"
                className="absolute right-3 top-3 p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Included support time</dt>
                  <dd className="font-medium">
                    {active.includedIncrements} increments ({(active.includedIncrements * 20) / 60}{' '}
                    hrs / cycle)
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Out-of-scope rate</dt>
                  <dd className="font-medium">
                    {USD.format(Number(active.customRate))} per extra 20-min increment
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Billing cycle</dt>
                  <dd className="font-medium">
                    Day {active.cycleDay} → day {active.cycleDay} monthly
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Start date</dt>
                  <dd className="font-medium">{new Date(active.startDate).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Covers website</dt>
                  <dd className="font-medium">
                    {websiteName(active.websiteId) ?? 'All / not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">GA4 property</dt>
                  <dd className="font-medium">{active.gaPropertyId ?? 'Company default'}</dd>
                </div>
              </div>
            </>
          )}

          {showForm && (
            <div className="space-y-4">
              {!active && (
                <p className="text-sm text-muted-foreground">
                  No agreement yet — set one up. Included increments cover routine support; work
                  beyond them (out-of-scope) bills at the rate you set here.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="agr-increments">Included support time (increments)</Label>
                  <Input
                    id="agr-increments"
                    type="number"
                    min={0}
                    value={includedIncrements}
                    onChange={(e) => setIncludedIncrements(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    20-minute blocks included each cycle. 12 increments = 4 hours.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agr-rate">Out-of-scope rate (USD)</Label>
                  <Input
                    id="agr-rate"
                    type="text"
                    placeholder="60.00"
                    value={customRate}
                    onChange={(e) => setCustomRate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Billed per extra 20-minute increment beyond the included time.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agr-cycle">Billing cycle</Label>
                  <select
                    id="agr-cycle"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={cycleDay}
                    onChange={(e) => setCycleDay(e.target.value)}
                  >
                    <option value="27">27th → 27th</option>
                    <option value="1">1st → 1st</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    The day each month when included time resets.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agr-start">Start date</Label>
                  <Input
                    id="agr-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">When this agreement takes effect.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agr-website">Covers website</Label>
                  <select
                    id="agr-website"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={websiteId}
                    onChange={(e) => setWebsiteId(e.target.value)}
                  >
                    <option value="">All / not set</option>
                    {websites.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.label ?? w.url}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    With multiple sites, add one agreement per website.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="agr-ga">GA4 property ID (optional)</Label>
                  <Input
                    id="agr-ga"
                    type="text"
                    placeholder="e.g. 312345678"
                    value={gaPropertyId}
                    onChange={(e) => setGaPropertyId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Analytics property for this agreement's monthly report.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={busy}>
                  {busy ? 'Saving…' : 'Save agreement'}
                </Button>
                {active && (
                  <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium uppercase text-muted-foreground mb-1">
          Past agreements
        </h3>
        <p className="text-xs text-muted-foreground mb-2">
          Superseded agreements, kept read-only for billing history.
        </p>
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">End</th>
                <th className="px-4 py-3 font-medium">Rate</th>
                <th className="px-4 py-3 font-medium">Included</th>
                <th className="px-4 py-3 font-medium">Cycle day</th>
                <th className="px-4 py-3 font-medium">Website</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No past agreements.
                  </td>
                </tr>
              )}
              {history.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(a.startDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {a.endDate ? new Date(a.endDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">{USD.format(Number(a.customRate))}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.includedIncrements}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.cycleDay}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {websiteName(a.websiteId) ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
